import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "fila_inteligente.media_sound_enabled";
const DECIDED_KEY = "fila_inteligente.media_sound_decided";

/**
 * Hook to manage media sound preference.
 * Returns { enabled, decided, setSound, toggle }.
 *
 * - enabled: boolean — current state of audio
 * - decided: boolean — true if user already chose (yes/no). False on first ever load.
 * - setSound(bool): set explicitly + mark as decided
 * - toggle(): flip current state + mark as decided
 *
 * Default false: browsers require a user gesture before autoplay with sound.
 */
export const useMediaSound = () => {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [decided, setDecided] = useState(() => {
    try {
      return localStorage.getItem(DECIDED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const persist = (next) => {
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      localStorage.setItem(DECIDED_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const setSound = useCallback((value) => {
    const next = !!value;
    setEnabled(next);
    setDecided(true);
    persist(next);
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      setDecided(true);
      persist(next);
      return next;
    });
  }, []);

  // Sync across tabs/windows
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setEnabled(e.newValue === "1");
      if (e.key === DECIDED_KEY) setDecided(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return { enabled, decided, setSound, toggle };
};
